import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { equipmentApi, projectsApi, usersApi, type EquipmentProjectOption } from '@/lib/apiClient';
import { StateView, Badge, Button } from '@/components';
import type { BadgeTone } from '@/components';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import EquipmentRequestForm from '../equipment/EquipmentRequestForm';
import { CallOffModal, ExtendModal, type Equipment } from '../equipment/EquipmentPage';

// Rows render as the full Equipment shape from the global module — the
// list endpoint returns every field (see EquipmentService.mapRow), and the
// call-off / extend modals need the richer fields (projectId, notes,
// altDeliveryAddress, billingCycle, …) beyond the columns shown here.
type EquipmentRow = Equipment;

interface ProjectRef { id: number; jobNumber: string; title: string }

// Per-row action target: which modal to open for which equipment item.
type ActionTarget = { kind: 'calloff' | 'extend'; item: Equipment };

// Map the equipment lifecycle to the shared Badge tones so the status pill
// matches the rest of the workspace. Mirrors the global Equipment page's map.
//
// Keyed on the status CODE, never the label: labels get reworded (On Rent →
// On-site, Returned → Complete), and a label-keyed map silently falls through
// to the neutral default on the next rename (#306).
const STATUS_TONE: Record<string, BadgeTone> = {
  requested:  'info',
  ordered:    'warning',
  on_site:    'success',
  called_off: 'danger',
  complete:   'neutral',
  canceled:   'neutral',
  approved:   'success', // retired, but old requests still render
};

const css = `
  .eq-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .eq-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .eq-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .eq-table tr:last-child td { border-bottom: 0; }
  .eq-muted { color: var(--ew-n-500); }
`;

export default function EquipmentTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['equipment', projectId],
    queryFn: () => equipmentApi.list({ projectId }) as Promise<EquipmentRow[]>,
  });

  // Project identity to pre-scope + lock the request form to this job. Shares
  // the warm workspace ['project', id] cache.
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<ProjectRef>,
    enabled: Number.isFinite(projectId),
    staleTime: 60_000,
  });
  const thisProject: ProjectRef = {
    id: projectId,
    jobNumber: projectQuery.data?.jobNumber ?? '',
    title: projectQuery.data?.title ?? '',
  };

  // Project-options (field leader / address / site contact) so the request
  // form can surface this job's info panel. Unscoped list shared with the
  // global Equipment page; long staleTime.
  const { data: projectOptions = [] } = useQuery<EquipmentProjectOption[]>({
    queryKey: ['equipment-project-options'],
    queryFn: () => equipmentApi.projectOptions(),
    staleTime: 300_000,
  });

  // Permission gating — POST /equipment needs 'equipment.edit'; Admin
  // short-circuits. Without it, the list is read-only (no request button).
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  // Activity-locked (SYSTAC / ClosedWon) projects can't take equipment requests
  // until a Sage job number lands — read-only regardless of permission.
  const canEdit = ((me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('equipment.edit') ?? false)) && !locked;

  const [showRequest, setShowRequest] = useState(false);
  const [action, setAction] = useState<ActionTarget | null>(null);

  const onSaved = () => {
    qc.invalidateQueries({ queryKey: ['equipment', projectId] });
    qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
    setShowRequest(false);
  };

  // Call-off / extend write status + notes back to the same equipment row, so
  // just refresh this job's list and close.
  const onActionSaved = () => {
    qc.invalidateQueries({ queryKey: ['equipment', projectId] });
    setAction(null);
  };

  return (
    <>
      <style>{css}</style>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 'var(--ew-s-4)' }}>
        {canEdit ? <button className="btn btn-primary" onClick={() => setShowRequest(true)}>+ Request equipment</button> : null}
      </div>

      <div className="pw-card">
        <h2 className="pw-card-hd">Equipment</h2>
        <StateView query={query} empty="No equipment on this job yet.">
          {(rows) => (
            <div className="ew-table-scroll">
            <table className="eq-table ew-cards">
              <thead>
                <tr>
                  <th>Equipment Type</th>
                  <th>Status</th>
                  <th>Vendor</th>
                  <th>Delivery</th>
                  <th>End</th>
                  <th>Rental/Owned</th>
                  <th>Owned Eq #</th>
                  <th>Requested By</th>
                  <th>Submitted</th>
                  {canEdit ? <th style={{ textAlign: 'right' }}>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id}>
                    <td data-label="Equipment Type">{e.equipmentType ?? '—'}</td>
                    <td data-label="Status"><Badge label={e.status.label} tone={STATUS_TONE[e.status.code] ?? 'neutral'} /></td>
                    <td className="eq-muted" data-label="Vendor">{e.vendor ?? '—'}</td>
                    <td className="eq-muted" data-label="Delivery" style={{ whiteSpace: 'nowrap' }}>{fmtDate(e.deliveryDate)}</td>
                    <td className="eq-muted" data-label="End" style={{ whiteSpace: 'nowrap' }}>{fmtDate(e.endDate)}</td>
                    <td className="eq-muted" data-label="Rental/Owned">{e.rentalType?.label ?? '—'}</td>
                    <td className="eq-muted" data-label="Owned Eq #">{e.ownedEquipmentNumber ?? '—'}</td>
                    <td className="eq-muted" data-label="Requested By">{e.requestedBy ?? '—'}</td>
                    <td className="eq-muted" data-label="Submitted" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.createdAt)}</td>
                    {canEdit ? (
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', gap: 'var(--ew-s-2)', justifyContent: 'flex-end' }}>
                          {e.status.code === 'on_site' ? (
                            <Button variant="danger" size="sm" onClick={() => setAction({ kind: 'calloff', item: e })}>
                              Call Off
                            </Button>
                          ) : null}
                          {e.status.code === 'on_site' || e.status.code === 'called_off' ? (
                            <Button variant="secondary" size="sm" onClick={() => setAction({ kind: 'extend', item: e })}>
                              Extend
                            </Button>
                          ) : null}
                        </span>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </StateView>
      </div>

      {/* Full edit / call-off / extend live in the global Equipment module. */}
      <p style={{ marginTop: 'var(--ew-s-3)', font: 'var(--ew-fw-regular) 12px/1.4 var(--ew-font)', color: 'var(--ew-n-500)' }}>
        Manage rentals (call-off, extend, edit details) in the{' '}
        <a href="/equipment" style={{ color: 'var(--ew-green)' }}>Equipment module</a>.
      </p>

      {showRequest && (
        <EquipmentRequestForm
          projects={projectOptions}
          lockedProject={thisProject}
          onClose={() => setShowRequest(false)}
          onSaved={onSaved}
        />
      )}

      {/* Call-off / extend reuse the global Equipment module's modals. CallOffModal
          prefills pickup/site-contact from the matching project option, so pass the
          same unscoped project-options list (EquipmentProjectOption[]) it expects. */}
      {action?.kind === 'calloff' && (
        <CallOffModal
          item={action.item}
          projects={projectOptions}
          onClose={() => setAction(null)}
          onSaved={onActionSaved}
        />
      )}

      {action?.kind === 'extend' && (
        <ExtendModal
          item={action.item}
          onClose={() => setAction(null)}
          onSaved={onActionSaved}
        />
      )}
    </>
  );
}
